import { 
  Asset, 
  AssetResponse, 
  AssetsResponse, 
  CreateAssetRequest, 
  AssignAssetRequest,
  UpdateAssetRequest,
  UpdateAssetStatusRequest,
  AssetStatus
} from '../../types/asset.types';
import apiService from '../api/api.service';

class AssetService {
  private static instance: AssetService;

  private constructor() {}

  public static getInstance(): AssetService {
    if (!AssetService.instance) {
      AssetService.instance = new AssetService();
    }
    return AssetService.instance;
  }

  /**
   * Yeni zimmet oluşturur (Envantere ekler)
   * @param assetData - Zimmet bilgileri
   * @returns Promise<Asset>
   */
  public async createAsset(assetData: CreateAssetRequest): Promise<Asset> {
    // Eğer dosya varsa FormData kullan
    if (assetData.files && assetData.files.length > 0) {
      const formData = new FormData();
      formData.append('name', assetData.name);
      formData.append('serialNumber', assetData.serialNumber);
      
      // Dosyaları ekle (max 5 adet)
      Array.from(assetData.files).slice(0, 5).forEach(file => {
        formData.append('files', file);
      });

      const response = await apiService.postFormData<Asset>('/assets', formData);
      return response;
    } else {
      // Dosya yoksa normal JSON
      const response = await apiService.post<Asset>('/assets', {
        name: assetData.name,
        serialNumber: assetData.serialNumber
      });
      return response;
    }
  }

    /**
   * Belirli bir kullanıcının zimmetlerini getirir
   * @param userId - Kullanıcı ID'si
   * @returns Promise<Asset[]>
   */
  public async getUserAssets(userId: number): Promise<Asset[]> {
    const response = await apiService.get<Asset[]>(`/assets/user/${userId}`);
    return response;
  }

  /**
   * Tüm zimmetleri getirir (admin için)
   * @returns Promise<Asset[]>
   */
  public async getAllAssets(): Promise<Asset[]> {
    const response = await apiService.get<Asset[]>('/assets');
    return response;
  }

  /**
   * Zimmeti bir çalışana atar
   * @param assetId - Zimmet ID'si
   * @param assignData - Atama bilgileri
   * @returns Promise<Asset>
   */
  public async assignAsset(assetId: number, assignData: AssignAssetRequest): Promise<Asset> {
    const response = await apiService.patch<Asset>(`/assets/assign/${assetId}`, assignData);
    return response;
  }

  /**
   * Zimmeti çalışandan geri alır
   * @param assetId - Zimmet ID'si
   * @returns Promise<Asset>
   */
  public async returnAsset(assetId: number): Promise<Asset> {
    const response = await apiService.patch<Asset>(`/assets/return/${assetId}`, {});
    return response;
  }

  /**
   * Zimmet bilgilerini günceller (ad ve seri numarası)
   * @param assetId - Zimmet ID'si
   * @param updateData - Güncellenecek bilgiler
   * @returns Promise<Asset>
   */
  public async updateAsset(assetId: number, updateData: UpdateAssetRequest): Promise<Asset> {
    const response = await apiService.patch<Asset>(`/assets/${assetId}`, updateData);
    return response;
  }

  /**
   * Zimmet dosyalarını günceller
   * @param assetId - Zimmet ID'si
   * @param existingFiles - Mevcut dosyalar (JSON string)
   * @param newFiles - Yeni dosyalar
   * @returns Promise<Asset>
   */
  public async updateAssetFiles(
    assetId: number, 
    existingFiles: { path: string; fileName: string; mimeType: string; size: number }[], 
    newFiles?: File[]
  ): Promise<Asset> {
    const formData = new FormData();
    
    // Mevcut dosyaları JSON string olarak ekle
    formData.append('existingFiles', JSON.stringify(existingFiles));
    
    // Yeni dosyaları ekle
    if (newFiles && newFiles.length > 0) {
      Array.from(newFiles).forEach(file => {
        formData.append('newFiles', file);
      });
    }

    const response = await apiService.putFormData<Asset>(`/assets/${assetId}/files`, formData);
    return response;
  }

  /**
   * Zimmet durumunu günceller (örn: hurdaya ayır)
   * @param assetId - Zimmet ID'si
   * @param statusData - Yeni durum bilgisi
   * @returns Promise<Asset>
   */
  public async updateAssetStatus(assetId: number, statusData: UpdateAssetStatusRequest): Promise<Asset> {
    const response = await apiService.put<Asset>(`/assets/${assetId}/status`, statusData);
    return response;
  }

  /**
   * Zimmeti siler
   * @param assetId - Zimmet ID'si
   * @returns Promise<void>
   */
  public async deleteAsset(assetId: number): Promise<void> {
    await apiService.delete(`/assets/${assetId}`);
  }

  /**
   * Kullanıcının iade edilmemiş zimmetlerini kontrol eder
   * @param userId - Kullanıcı ID'si
   * @returns Promise<{hasUnreturnedAssets: boolean, assets: Asset[]}>
   */
  public async checkUserUnreturnedAssets(userId: number): Promise<{
    hasUnreturnedAssets: boolean;
    assets: Asset[];
  }> {
    const userAssets = await this.getUserAssets(userId);

    const unreturnedAssets = userAssets.filter(asset => asset.status === AssetStatus.ASSIGNED);
    
    return {
      hasUnreturnedAssets: unreturnedAssets.length > 0,
      assets: unreturnedAssets
    };
  }
}

export default AssetService.getInstance(); 